"use client";

/**
 * "Time in stage" — the deal-detail card that answers *how long has this been
 * sitting here*, from `deal_stage_history` rather than `deals.updated_at`.
 *
 * `updated_at` moves on any edit, so the old proxy always flattered the deal
 * (a retag made a three-month-old proposal look like today's work). This reads
 * the transition log, so the headline number is measured, and a backfilled
 * seed row is labelled "at least" instead of pretending to be exact.
 *
 * Renders nothing at all when there is no history and no error — a deal whose
 * history has not been seeded should not sprout an empty card.
 */

import React from "react";

import { describeStageDuration } from "@west-tennessee-consulting/deal-stages";
import { Badge } from "@west-tennessee-consulting/ui/components/badge";
import { Card, CardContent } from "@west-tennessee-consulting/ui/components/card";

import { dealStageLabel } from "@/lib/constants/deal-stage-display";

import { useDealStageHistory } from "./use-deal-stage-history";

/** Prior transitions shown under the headline; the rest stay in the log. */
const RECENT_TRANSITIONS = 4;

export function DealTimeInStageCard({ dealId }: { dealId: string }) {
  const { loading, entries, current, error, retry } = useDealStageHistory(dealId);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm bg-gray-50 dark:bg-gray-900/40">
        <CardContent className="pt-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Time in Stage
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm bg-gray-50 dark:bg-gray-900/40">
        <CardContent className="pt-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Time in Stage
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">{error}</p>
          {/* ERROR_STATES_ACTIONABLE: offer the fix, don't just describe it. */}
          <button
            type="button"
            onClick={retry}
            className="mt-2 text-sm font-medium text-blue-700 dark:text-blue-300 underline"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  if (!current) return null;

  const previous = entries.slice(1, 1 + RECENT_TRANSITIONS);

  return (
    <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-950/40 dark:to-slate-900/40">
      <CardContent className="pt-4">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
          Time in Stage
        </p>
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {describeStageDuration(current)}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          in {dealStageLabel(current.toStage)} since{" "}
          {new Date(current.changedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        {current.isApproximate && (
          <Badge className="mt-2 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 text-[11px] font-medium">
            Estimated — recorded before stage tracking began
          </Badge>
        )}

        {previous.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Earlier stages
            </p>
            {previous.map((entry) => (
              <div key={entry.id} className="flex justify-between items-baseline gap-3">
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                  {dealStageLabel(entry.toStage)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {describeStageDuration(entry)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
